<template>
  <div class="number-line-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <marker id="arrow-nt-blue" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                 <path d="M0,0 L0,6 L9,3 z" fill="#3b82f6" />
              </marker>
           </defs>

           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 数轴基础 -->
           <g transform="translate(0, 300)">
              <line x1="50" y1="0" x2="750" y2="0" stroke="#475569" stroke-width="4" marker-end="url(#arrow-nt-blue)" />
              
              <!-- 刻度线 -->
              <g v-for="i in (mode === 'rational' ? 21 : 11)" :key="'tick'+i">
                 <line :x1="400 + (i - (mode === 'rational' ? 11 : 6)) * (mode === 'rational' ? 30 : 60)" 
                       y1="-10" :x2="400 + (i - (mode === 'rational' ? 11 : 6)) * (mode === 'rational' ? 30 : 60)" y2="10" stroke="#94a3b8" stroke-width="2.5" />
                 <text :x="400 + (i - (mode === 'rational' ? 11 : 6)) * (mode === 'rational' ? 30 : 60)" y="35" fill="#94a3b8" font-size="16" text-anchor="middle" font-weight="bold">
                    {{ (i - (mode === 'rational' ? 11 : 6)) * (mode === 'rational' ? 0.5 : 1) }}
                 </text>
              </g>

              <!-- 原点 O -->
              <circle cx="400" cy="0" r="6" fill="#fff" stroke="#3b82f6" stroke-width="3" />
              <text x="400" y="-20" fill="#fff" font-size="20" text-anchor="middle" font-weight="900">0 (原点)</text>

              <!-- 当前点 P (X) -->
              <g :transform="`translate(${400 + xVal * 60}, 0)`">
                 <circle cx="0" cy="0" r="10" fill="#3b82f6" stroke="#fff" stroke-width="2.5" style="filter: drop-shadow(0 0 12px #3b82f6)" />
                 <text x="0" y="-40" fill="#3b82f6" font-size="26" text-anchor="middle" font-weight="900">点 P = {{ xVal }}</text>
              </g>

              <!-- 相反数点 (-X) -->
              <g v-if="mode === 'opposite'" :transform="`translate(${400 - xVal * 60}, 0)`">
                 <circle cx="0" cy="0" r="10" fill="#ef4444" stroke="#fff" stroke-width="2.5" />
                 <text x="0" y="-40" fill="#fca5a5" font-size="26" text-anchor="middle" font-weight="900">相反数 = {{ -xVal }}</text>
              </g>

              <!-- 绝对值距离展示 -->
              <g v-if="mode === 'absolute' && xVal !== 0">
                 <path :d="`M 400,70 Q ${400 + xVal * 30},120 ${400 + xVal * 60},70`" fill="none" stroke="#10b981" stroke-width="4" stroke-dasharray="10,5" />
                 <text :x="400 + xVal * 30" y="140" fill="#10b981" font-size="24" text-anchor="middle" font-weight="900">绝对值 |{{ xVal }}| = {{ Math.abs(xVal) }}</text>
              </g>
           </g>
           
           <text x="50" y="550" fill="rgba(255,255,255,0.3)" font-size="16" font-weight="bold">数轴性质演示视图</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>当前坐标</label>
              <span class="val">{{ xVal }}</span>
           </div>
           <div class="hud-item cyan" v-if="mode === 'absolute'">
              <label>距原点距离</label>
              <span class="val">{{ Math.abs(xVal) }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>调整点坐标 P ({{ xVal }})</label>
                 <el-slider v-model="xVal" :min="-5" :max="5" :step="0.5" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>演示主题切换</label>
                 <el-radio-group v-model="mode" size="mini">
                   <el-radio-button label="basic">基础</el-radio-button>
                   <el-radio-button label="opposite">相反数</el-radio-button>
                   <el-radio-button label="absolute">绝对值</el-radio-button>
                   <el-radio-button label="rational">有理数</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>数轴本质：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NumberLineViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'basic',
      xVal: 2
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       const maps = {
          basic: '数轴基础：数值与空间位置的一一对应',
          opposite: '相反数：关于原点对称的平衡属性探索',
          absolute: '绝对值：数轴上点到原点 O 的纯粹距离',
          rational: '有理数定位：在数轴上识别分数与小数'
       };
       return maps[this.mode] || '数轴性质仿真';
    },
    activeFormula() {
       if (this.mode === 'opposite') return 'x + (-x) = 0';
       if (this.mode === 'absolute') return '|x| = 距离(x, 0)';
       return 'x ∈ ℝ (实数)';
    },
    modeDesc() {
       const maps = {
          basic: '数轴由原点、正方向和单位长度组成，是所有代数运算的几何基础',
          opposite: '符号相反、数值相等的两个数互为相反数，它们处于原点两侧等距位置',
          absolute: '绝对值表示点到原点的距离，它不具备方向性，因此绝对值恒非负',
          rational: '整数、分数与小数在数轴上都有唯一的定位点，细分单位刻度即可找到它们'
       };
       return maps[this.mode] || '';
    },
    tipText() {
       return '拖动蓝点，观察坐标变化及其对应的几何关联（如对称点或测距弧）。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/相反数|对称/.test(kp)) this.mode = 'opposite';
      else if (/绝对值|距离/.test(kp)) this.mode = 'absolute';
      else if (/分数|小数|有理数/.test(kp)) this.mode = 'rational';
      else this.mode = 'basic';
    }
  }
}
</script>

<style scoped lang="scss">
.number-line-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 5px 16px; border-radius: 20px; font-weight: 900; font-family: "Courier New", monospace; font-size: 14px; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #334155; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 900; } &.blue { border-left: 4px solid #3b82f6; } &.cyan { border-left: 4px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 4px; display: block; } } }
.discovery-card-compact { background: #f8fafc; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #334155; line-height: 1.6; strong { color: #0f172a; font-weight: 900; } } } }
</style>
